import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import icon from 'flarum/common/helpers/icon';
import LogInModal from 'flarum/forum/components/LogInModal';

import LoungePanel from './LoungePanel';
import ProfileDetailsModal from './ProfileDetailsModal';
import { tagFallbackImageUrl } from '../tagImage';
import pakt21ApiUrl from '../../common/pakt21ApiUrl';

const MOBILE_SPACES = [
  {
    key: 'market',
    icon: 'fas fa-handshake',
    links: [
      { slug: 'oferuje', labelKey: 'space_link_offer' },
      { slug: 'poszukuje', labelKey: 'space_link_contractor' },
      { slug: 'kupie', labelKey: 'space_link_buy' },
      { slug: 'sprzedam', labelKey: 'space_link_sell' },
    ],
  },
  {
    key: 'shared_cause',
    icon: 'fas fa-people-carry',
    links: [
      { slug: 'wielki-atlas-wybrancow-narodu', labelKey: 'space_link_atlas' },
      { slug: 'niewygodne', labelKey: 'space_link_uncomfortable' },
      { slug: 'potrzebuje-pomocy', labelKey: 'space_link_help' },
    ],
  },
  {
    key: 'build',
    icon: 'fas fa-tools',
    links: [{ slug: 'budujemy-pakt21', labelKey: 'space_link_enter' }],
  },
  {
    key: 'hobbies',
    icon: 'fas fa-compass',
    links: [{ slug: 'nasze-hobby', labelKey: 'space_link_hobbies' }],
  },
  {
    key: 'arena',
    icon: 'fas fa-balance-scale',
    links: [{ slug: 'arena-p21', labelKey: 'space_link_arena' }],
  },
];

function trans(key, input = {}) {
  return app.translator.trans(`pakt21-beta.forum.dashboard.${key}`, input);
}

function tagsOf(discussion) {
  return discussion.tags || [];
}

function tagSlug(tag) {
  return tag.slug;
}

function isAnnouncement(discussion) {
  return tagsOf(discussion).some((tag) =>
    ['ogloszenia', 'ogloszenia-administracji'].includes(tagSlug(tag))
  );
}

function discussionUrl(discussion) {
  return `/d/${discussion.slug || discussion.id}`;
}

function relativeTime(value) {
  if (!value) return '';

  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(document.documentElement.lang || 'pl', {
    numeric: 'auto',
  });

  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');

  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');

  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');

  return formatter.format(Math.round(hours / 24), 'day');
}

function discussionTag(discussion) {
  const [tag] = tagsOf(discussion);

  if (!tag) return null;

  const { name, color } = tag;

  return (
    <span className="Pakt21Topic-tag" style={color ? { '--pakt21-tag-color': color } : null}>
      {name}
    </span>
  );
}

function topicRow(discussion, compact = false) {
  const count = Number(discussion.commentCount || 0);
  const lastPostedAt = discussion.lastPostedAt || discussion.createdAt;

  return (
    <li className={`Pakt21Topic ${compact ? 'Pakt21Topic--compact' : ''}`.trim()} key={discussion.id}>
      <span className="Pakt21Topic-icon" aria-hidden="true">
        {icon(compact ? 'far fa-file-alt' : 'far fa-comment-dots')}
      </span>
      <div className="Pakt21Topic-main">
        <Link href={discussionUrl(discussion)} className="Pakt21Topic-title">
          {discussion.title}
        </Link>
        {discussionTag(discussion)}
      </div>
      <span className="Pakt21Topic-count" title={trans('reply_count_title')}>
        {count}
      </span>
      <time className="Pakt21Topic-time" datetime={lastPostedAt}>
        {relativeTime(lastPostedAt)}
      </time>
    </li>
  );
}

function topicCard(discussion, onBoost, onShare) {
  const count = Number(discussion.commentCount || 0);
  const boostCount = Number(discussion.boostCount || 0);
  const lastPostedAt = discussion.lastPostedAt || discussion.createdAt;
  const tagSlug = discussion.tags?.[0]?.slug;
  const imageUrl = discussion.imageUrl || (tagSlug ? tagFallbackImageUrl(tagSlug, 178, 112) : null);
  const isStockImage = !discussion.imageUrl;

  return (
    <article className="Pakt21FeedCard" key={discussion.id}>
      <div className="Pakt21FeedCard-content">
        <div className="Pakt21FeedCard-meta">
          {discussionTag(discussion)}
          <span>{discussion.authorName || trans('unknown_author')}</span>
          <span aria-hidden="true">·</span>
          <time datetime={lastPostedAt}>{relativeTime(lastPostedAt)}</time>
        </div>
        <div className="Pakt21FeedCard-preview">
          <div className="Pakt21FeedCard-previewCopy">
            <Link href={discussionUrl(discussion)} className="Pakt21FeedCard-title">
              {discussion.title}
            </Link>
            {discussion.excerpt && <p className="Pakt21FeedCard-excerpt">{discussion.excerpt}</p>}
          </div>
          <Link
            href={discussionUrl(discussion)}
            className={`Pakt21FeedCard-image ${isStockImage ? 'Pakt21FeedCard-image--stock' : ''}`.trim()}
          >
            <img src={imageUrl} alt="" loading="lazy" />
          </Link>
        </div>
        <footer className="Pakt21FeedCard-actions">
          <button
            type="button"
            className={`Pakt21FeedCard-action Pakt21FeedCard-boost ${discussion.hasBoosted ? 'is-active' : ''}`.trim()}
            title={trans('boost_help')}
            onclick={() => onBoost(discussion)}
          >
            {icon('fas fa-arrow-up')} <span>{trans('boost')}</span> <strong>{boostCount}</strong>
          </button>
          <Link href={discussionUrl(discussion)} className="Pakt21FeedCard-action Pakt21FeedCard-comments">
            {icon('far fa-comment-dots')} <span>{trans('comments', { count })}</span>
          </Link>
          <button
            type="button"
            className="Pakt21FeedCard-action Pakt21FeedCard-share"
            title={trans('share')}
            onclick={() => onShare(discussion)}
          >
            {icon('fas fa-share-alt')} <span>{discussion.shareCopied ? trans('share_copied') : trans('share')}</span>
          </button>
        </footer>
      </div>
    </article>
  );
}

function emptyState(content) {
  return <p className="Pakt21Dashboard-empty">{content}</p>;
}

function mobileSpaces() {
  return (
    <section className="Pakt21MobileSpaces" aria-labelledby="pakt21-mobile-spaces-title">
      <div className="Pakt21MobileSpaces-heading">
        <div>
          <span>{trans('spaces_kicker')}</span>
          <h2 id="pakt21-mobile-spaces-title">{trans('spaces_title')}</h2>
        </div>
        <Link href="/tags">{trans('spaces_all')} {icon('fas fa-arrow-right')}</Link>
      </div>
      <div className="Pakt21MobileSpaces-list">
        {MOBILE_SPACES.map((space) => (
          <article className={`Pakt21MobileSpace Pakt21MobileSpace--${space.key}`} key={space.key}>
            <span className="Pakt21MobileSpace-icon" aria-hidden="true">{icon(space.icon)}</span>
            <div className="Pakt21MobileSpace-copy">
              <h3>{trans(`space_${space.key}_title`)}</h3>
              <p>{trans(`space_${space.key}_description`)}</p>
              <div className="Pakt21MobileSpace-links">
                {space.links.map((link) => (
                  <Link href={`/t/${link.slug}`} key={link.slug}>
                    {trans(link.labelKey)}
                  </Link>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default class HomeDashboard extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    document.body.classList.add('Pakt21HomeRoute');
    this.homeDiscussions = [];
    this.discussionsLoading = true;
    this.activitySort = 'latest';
    this.loadDiscussions();
  }

  onremove() {
    document.body.classList.remove('Pakt21HomeRoute');
  }

  userName() {
    return app.session.user ? app.session.user.displayName() : null;
  }

  profileUrl() {
    if (!app.session.user) return null;

    return `/u/${app.session.user.attribute('username')}`;
  }

  profileIsEmpty() {
    const user = app.session.user;

    if (!user) return false;

    return ![
      user.attribute('pakt21About'),
      user.attribute('pakt21Contribution'),
      user.attribute('pakt21Needs'),
      user.attribute('pakt21Location'),
      ...(user.attribute('pakt21Skills') || []),
    ].some((value) => String(value || '').trim());
  }

  openProfileSetup() {
    app.modal.show(ProfileDetailsModal, { user: app.session.user });
  }

  isDarkTheme() {
    return document.documentElement.getAttribute('data-pakt21-theme') === 'dark';
  }

  toggleTheme() {
    const next = this.isDarkTheme() ? 'light' : 'dark';

    document.documentElement.setAttribute('data-pakt21-theme', next);
    window.localStorage.setItem('pakt21-theme', next);
  }

  loadDiscussions() {
    app
      .request({
        method: 'GET',
        url: pakt21ApiUrl(app.forum.attribute('apiUrl'), '/discussions?include=tags,user,firstPost&page[limit]=20'),
      })
      .then((payload) => {
        const tags = new Map(
          (payload.included || [])
            .filter((item) => item.type === 'tags')
            .map((item) => [String(item.id), {
              slug: item.attributes.slug,
              name: item.attributes.name,
              color: item.attributes.color,
            }])
        );
        const users = new Map(
          (payload.included || [])
            .filter((item) => item.type === 'users')
            .map((item) => [String(item.id), item.attributes.displayName || item.attributes.username])
        );
        const posts = new Map(
          (payload.included || [])
            .filter((item) => item.type === 'posts')
            .map((item) => [String(item.id), item.attributes.contentHtml || item.attributes.content || ''])
        );

        this.homeDiscussions = (payload.data || [])
          .filter((item) => !item.attributes.isHidden)
          .map((item) => {
            const preview = this.postPreview(posts.get(String(item.relationships?.firstPost?.data?.id)) || '');

            return {
              id: String(item.id),
              title: item.attributes.title,
              slug: item.attributes.slug,
              commentCount: item.attributes.commentCount,
              createdAt: item.attributes.createdAt,
              lastPostedAt: item.attributes.lastPostedAt,
              boostCount: item.attributes.pakt21BoostCount || 0,
              hasBoosted: Boolean(item.attributes.pakt21HasBoosted),
              authorName: users.get(String(item.relationships?.user?.data?.id)) || null,
              excerpt: preview.excerpt,
              imageUrl: item.attributes.pakt21FeaturedImageUrl || preview.imageUrl,
              tags: (item.relationships?.tags?.data || [])
                .map((relation) => tags.get(String(relation.id)))
                .filter(Boolean),
            };
          });
      })
      .catch(() => {
        this.homeDiscussions = [];
      })
      .finally(() => {
        this.discussionsLoading = false;
        m.redraw();
      });
  }

  postPreview(html) {
    if (!html) return { excerpt: '', imageUrl: null };

    const holder = document.createElement('div');
    holder.innerHTML = html;
    const imageSource = holder.querySelector('img')?.getAttribute('src') || '';
    const imageUrl = /^(https?:\/\/|\/)/i.test(imageSource) ? imageSource : null;

    return {
      excerpt: String(holder.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 280),
      imageUrl,
    };
  }

  sortedActivity() {
    const discussions = this.homeDiscussions.slice();

    if (this.activitySort === 'popular') {
      return discussions.sort((left, right) => {
        return Number(right.boostCount || 0) - Number(left.boostCount || 0)
          || Number(right.commentCount || 0) - Number(left.commentCount || 0)
          || new Date(right.lastPostedAt || right.createdAt) - new Date(left.lastPostedAt || left.createdAt);
      });
    }

    return discussions.sort((left, right) =>
      new Date(right.lastPostedAt || right.createdAt) - new Date(left.lastPostedAt || left.createdAt)
    );
  }

  toggleBoost(discussion) {
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }

    app.request({
      method: 'POST',
      url: pakt21ApiUrl(app.forum.attribute('apiUrl'), `/pakt21/discussions/${discussion.id}/boost`),
    }).then((payload) => {
      discussion.boostCount = Number(payload.data.attributes.count || 0);
      discussion.hasBoosted = Boolean(payload.data.attributes.boosted);
      m.redraw();
    });
  }

  shareDiscussion(discussion) {
    const url = new URL(discussionUrl(discussion), window.location.origin).toString();

    if (navigator.share) {
      navigator.share({ title: discussion.title, url }).catch(() => {});
      return;
    }

    navigator.clipboard.writeText(url).then(() => {
      discussion.shareCopied = true;
      m.redraw();
      window.setTimeout(() => {
        discussion.shareCopied = false;
        m.redraw();
      }, 1800);
    });
  }

  view() {
    const availableDiscussions = this.homeDiscussions;
    const announcements = availableDiscussions.filter(isAnnouncement).slice(0, 3);
    const activity = this.sortedActivity().slice(0, 20);
    const name = this.userName();

    return (
      <div className="Pakt21HomeDashboard">
        <div className="Pakt21HomeDashboard-inner">
          <main className="Pakt21Dashboard-main">
            <LoungePanel />

            <header className="Pakt21Dashboard-welcome">
              <div>
                <h1>{name ? trans('welcome_user', { username: name }) : trans('welcome_guest')}</h1>
                <p>{trans('welcome_subtitle')}</p>
              </div>
              <div className="Pakt21Dashboard-actions">
                <button
                  type="button"
                  className="Button Button--icon Pakt21ThemeToggle"
                  title={this.isDarkTheme() ? trans('enable_light_theme') : trans('enable_dark_theme')}
                  aria-label={this.isDarkTheme() ? trans('enable_light_theme') : trans('enable_dark_theme')}
                  onclick={() => {
                    this.toggleTheme();
                    m.redraw();
                  }}
                >
                  {icon(this.isDarkTheme() ? 'fas fa-sun' : 'fas fa-moon')}
                </button>
                {name && (
                  <Link className="Button Pakt21Dashboard-profileButton" href={this.profileUrl()}>
                    {trans('edit_profile')}
                  </Link>
                )}
              </div>
            </header>

            {this.profileIsEmpty() && (
              <section className="Pakt21Onboarding" aria-labelledby="pakt21-onboarding-title">
                <span className="Pakt21Onboarding-icon" aria-hidden="true">{icon('fas fa-user-circle')}</span>
                <div className="Pakt21Onboarding-copy">
                  <span>{trans('onboarding_kicker')}</span>
                  <h2 id="pakt21-onboarding-title">{trans('onboarding_title')}</h2>
                  <p>{trans('onboarding_description')}</p>
                  <strong>
                    {app.session.user.attribute('pakt21MemberNumber')
                      ? trans('onboarding_id', { number: app.session.user.attribute('pakt21MemberNumber') })
                      : trans('onboarding_id_pending')}
                  </strong>
                </div>
                <button type="button" className="Button Button--primary" onclick={() => this.openProfileSetup()}>
                  {trans('onboarding_action')}
                </button>
              </section>
            )}

            <section className="Pakt21ReputationIntro" aria-labelledby="pakt21-reputation-title">
              <span className="Pakt21ReputationIntro-icon" aria-hidden="true">{icon('fas fa-shield-alt')}</span>
              <div>
                <span className="Pakt21ReputationIntro-kicker">{trans('reputation_kicker')}</span>
                <h2 id="pakt21-reputation-title">{trans('reputation_title')}</h2>
                <p>{trans('reputation_description')}</p>
              </div>
            </section>

            {mobileSpaces()}

            <section className="Pakt21Dashboard-card Pakt21Activity" aria-labelledby="pakt21-activity-title">
              <div className="Pakt21Dashboard-sectionHeader">
                <h2 id="pakt21-activity-title">{trans('activity_title')}</h2>
                <div className="Pakt21FeedSort" role="group" aria-label={trans('sort_label')}>
                  {['latest', 'popular'].map((sort) => (
                    <button
                      type="button"
                      className={this.activitySort === sort ? 'is-active' : ''}
                      onclick={() => {
                        this.activitySort = sort;
                        m.redraw();
                      }}
                    >
                      {trans(`sort_${sort}`)}
                    </button>
                  ))}
                </div>
              </div>
              {activity.length ? (
                <div className="Pakt21FeedList">
                  {activity.map((discussion) => topicCard(
                    discussion,
                    (item) => this.toggleBoost(item),
                    (item) => this.shareDiscussion(item)
                  ))}
                </div>
              ) : (
                emptyState(trans('activity_empty'))
              )}
            </section>

          </main>

          <aside className="Pakt21Dashboard-rail" aria-label={trans('forum_information')}>
            <section className="Pakt21Dashboard-card Pakt21RailCard">
              <div className="Pakt21Dashboard-sectionHeader">
                <h2>{trans('announcements_title')}</h2>
                <Link href="/t/ogloszenia-administracji">
                  {trans('all')} {icon('fas fa-arrow-right')}
                </Link>
              </div>
              {announcements.length ? (
                <ol className="Pakt21AnnouncementList">
                  {announcements.map((discussion) => topicRow(discussion, true))}
                </ol>
              ) : (
                emptyState(
                  trans('announcements_empty')
                )
              )}
            </section>

            <a
              className="Pakt21Dashboard-card Pakt21YoutubeCard"
              href="https://www.youtube.com/@pakt21com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="Pakt21YoutubeCard-icon">{icon('fab fa-youtube')}</span>
              <span className="Pakt21YoutubeCard-copy">
                <strong>{trans('youtube_title')}</strong>
                <span>{trans('youtube_handle')}</span>
                <small>{trans('youtube_description')}</small>
              </span>
              <span className="Pakt21YoutubeCard-arrow">{icon('fas fa-arrow-right')}</span>
            </a>

            <section className="Pakt21Dashboard-card Pakt21CommunityNote">
              <span className="Pakt21CommunityNote-icon">{icon('fas fa-people-carry')}</span>
              <div>
                <strong>{trans('community_note_title')}</strong>
                <p>{trans('community_note_description')}</p>
              </div>
            </section>
          </aside>
        </div>
        <div id="pakt21-full-forum" className="Pakt21FullForumAnchor" aria-hidden="true" />
      </div>
    );
  }
}
